<template>
  <ContentWrapperControl>
    <!-- Registrants List -->
    <div
      v-for="(registrant, index) in registrants"
      :key="index"
      class="bg-white p-4 rounded shadow overflow-hidden mt-4 mb-4"
    >
      <div>
        <div class="mb-4">
          <FormTextControl
            v-model="registrant.lname"
            name="lname"
            :placeholder="lang('lbl_0000145')"
            :max_length="24"
            :uppercase="true"
            disabled
          />
        </div>
        <div class="mb-4">
          <FormTextControl
            v-model="registrant.fname"
            name="fname"
            :placeholder="lang('lbl_0000146')"
            :max_length="24"
            :uppercase="true"
            disabled
          />
        </div>
        <div class="mb-4">
          <FormTextControl
            v-model="registrant.mname"
            name="mname"
            :placeholder="lang('lbl_0000325')"
            :max_length="24"
            :uppercase="true"
            disabled
          />
        </div>

        <div class="mb-4" v-if="registrant.account_no || registrant.serial_no">
          <FormTextControl
            v-model="registrant.account_no"
            name="account_no"
            :placeholder="lang('lbl_0000093')"
            :max_length="6"
            alphanumeric
            uppercase
            disabled
          />
          <FormTextControl
            class="mt-4"
            v-model="registrant.serial_no"
            name="serial_no"
            :placeholder="lang('lbl_0000094')"
            :max_length="12"
            alphanumeric
            uppercase
            disabled
          />
        </div>

        <div class="mb-4">
          <FormTextControl
            v-model="registrant.age"
            name="age"
            :placeholder="lang('lbl_0000103')"
            :max_length="2"
            disabled
          />
        </div>
        <div class="mb-4">
          <FormTextControl
            v-model="registrant.email"
            name="email"
            :placeholder="lang('lbl_0000005')"
            type="email"
            :max_length="50"
            disabled
          />
        </div>
        <div class="mb-4">
          <FormTextControl
            v-model="registrant.mobile"
            name="mobile"
            :placeholder="lang('lbl_0000007')"
            type="mobile"
            :max_length="10"
            disabled
          />
        </div>

        <div class="mb-4">
          <ComboboxControl
            name="gender"
            placeholder="Gender"
            :options="getGenders"
            v-model="registrant.gender"
            disabled
          />
        </div>

        <!-- Segment -->
        <div class="mb-4">
          <ComboboxControl
            name="segment"
            placeholder="Segment"
            :options="getSegments"
            v-model="registrant.segment"
            disabled
          />
        </div>

        <!-- Branch Service -->
        <div class="mb-4" v-if="showBranchService(registrant.segment)">
          <ComboboxControl
            name="branch_service"
            placeholder="Branch Service"
            :options="getBranchServicesBySegment(registrant.segment)"
            v-model="registrant.branch_service"
            disabled
          />
        </div>

        <!-- Sponsor Type -->
        <div class="mb-4" v-if="showSponsorType(registrant.segment)">
          <ComboboxControl
            name="sponsor_type"
            placeholder="Sponsor Type"
            :options="getSponsorTypes()"
            v-model="registrant.sponsor_type"
            disabled
          />
        </div>

        <!-- Category -->
        <div class="mb-4">
          <ComboboxControl
            name="category"
            placeholder="Category"
            :options="getRaceCategories"
            v-model="registrant.category"
            disabled
          />
        </div>

        <!-- Distance -->
        <div class="mb-4">
          <ComboboxControl
            name="distance"
            placeholder="Distance"
            :options="getDistanceBySegment(registrant.segment)"
            v-model="registrant.distance"
            disabled
          />
        </div>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="mt-4 flex space-x-2">
      <ButtonControl outline class="mr-3" @click="goToRegistration">
        {{ this.lang("lbl_0000246") }}
      </ButtonControl>
      <ButtonControl @click="register">
        {{ lang("lbl_0000113") }}
      </ButtonControl>
    </div>
  </ContentWrapperControl>
</template>

<script>
import ContentWrapperControl from "@/components/controls/ContentWrapperControl.vue";
import FormTextControl from "@/components/controls/FormTextControl.vue";
import ComboboxControl from "@/components/controls/ComboboxControl.vue";
import ButtonControl from "@/components/controls/ButtonControl.vue";
import FunRun from "@/services/FunRun.js";
import mixin from "./../../mixin.js";

export default {
  mixins: [mixin],
  components: {
    ContentWrapperControl,
    FormTextControl,
    ComboboxControl,
    ButtonControl,
  },
  mounted() {
    this.$emit("checkauth");
    this.$emit("shownavbar", {
      title: this.lang("lbl_0000328"),
      show: true,
      back: true,
    });
    this.$emit("nofooter", false);
  },
  computed: {
    registrants() {
      return this.$store.state.fun_run.registrants || [];
    },
    getSegments() {
      return this.$store.state.fun_run.segments || [];
    },
    getGenders() {
      return [
        { label: "Male", value: "M" },
        { label: "Female", value: "F" },
        { label: "Rather not say", value: "X" },
      ];
    },
    getRaceCategories() {
      return [
        { label: "Fun Run", value: "1" },
        { label: "Zumba", value: "2" },
        { label: "Both Fun Run & Zumba", value: "3" },
      ];
    },
  },
  methods: {
    getDistanceBySegment(segment_value) {
      return this.$store.state.fun_run.distances?.[segment_value] || [];
    },

    register() {
      this.$emit("showloader");

      const payload = this.$store.state.fun_run.registrants.map((reg) => {
        let mobile = reg.mobile || "";

        if (!mobile.startsWith("+63")) {
          if (mobile.startsWith("0")) {
            mobile = "+63" + mobile.slice(1);
          } else {
            mobile = "+63" + mobile;
          }
        }

        return {
          account_no: (reg.account_no || "").toUpperCase(),
          serial_no: (reg.serial_no || "").toUpperCase(),
          age: reg.age,
          branch_service: reg.branch_service,
          category: reg.category,
          distance: reg.distance,
          email: reg.email,
          fname: reg.fname,
          lname: reg.lname,
          mname: reg.mname,
          gender: reg.gender,
          mobile,
          segment: reg.segment,
          sponsor_type: reg.sponsor_type,
          guardian_name: reg.waiver?.guardian_name || "",
          relationship: reg.waiver?.relationship || "",
          date_signed:
            reg.date_signed || new Date().toISOString().split("T")[0],
        };
      });

      FunRun.register(payload, this.$store.state.uuid)
        .then(() => {
          this.$store.state.fun_run.registrants = [];
          this.$store.state.fun_run.is_editing = false;
          this.$emit("success", this.lang("lbl_0000327"));
        })
        .catch((error) => {
          this.$store.state.fun_run.registrants = [];
          this.$store.state.fun_run.is_editing = false;
          this.$emit("error", error);
        })
        .finally(() => {
          this.$emit("hideloader");
          this.$router.push("/dashboard");
          this.$store.state.is_pdf_open = false;
        });
    },
    goToRegistration() {
      this.$store.state.fun_run.is_editing = true;
      this.$router.push("/fun-run/registration");
    },

    showBranchService(segment_value) {
      return ["MEM", "EMP"].includes(segment_value);
    },

    getBranchServicesBySegment(segment_value) {
      switch (segment_value) {
        case "MEM":
          return [
            { label: "PHILIPPINE ARMY", value: "PA" },
            { label: "PHILIPPINE NAVY", value: "PN" },
            { label: "PHILIPPINE AIR FORCE", value: "PAF" },
            { label: "RETIREE", value: "RET" },
            { label: "DEPENDENT", value: "DEP" },
            { label: "CIVILIAN EMPLOYEE", value: "CE" },
          ];
        case "EMP":
          return [
            { label: "MAIN OFFICE", value: "HO" },
            { label: "PSO", value: "PSO" },
          ];
        default:
          return [];
      }
    },

    showSponsorType(segment_value) {
      return segment_value === "SPR";
    },

    getSponsorTypes() {
      return [
        { label: "PLATINUM", value: "P" },
        { label: "GOLD", value: "G" },
        { label: "SILVER", value: "S" },
        { label: "BRONZE", value: "B" },
      ];
    },
  },
};
</script>
